package com.bulut.mecitogullari.ui.screens.projectdetail

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.bulut.mecitogullari.core.i18n.Localization
import com.bulut.mecitogullari.core.i18n.unitDisplayLabel
import com.bulut.mecitogullari.core.util.todayUtcMidnightMillis
import com.bulut.mecitogullari.ui.excel.ExcelImportController
import com.bulut.mecitogullari.ui.excel.ExcelImportState
import com.bulut.mecitogullari.data.model.ApartmentUnit
import com.bulut.mecitogullari.data.model.CashTransaction
import com.bulut.mecitogullari.data.model.Project
import com.bulut.mecitogullari.data.model.TransactionType
import com.bulut.mecitogullari.data.model.UnitStatus
import com.bulut.mecitogullari.data.repository.BarterRepository
import com.bulut.mecitogullari.data.repository.ProjectRepository
import com.bulut.mecitogullari.data.repository.TransactionRepository
import com.bulut.mecitogullari.data.repository.UnitRepository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch

/** One building of the project with its floor grid. */
data class BlockGrid(
    /** Blank on single-block projects. */
    val name: String,
    /** Floor number → units on that floor, highest floor first. */
    val floors: List<Pair<Int, List<ApartmentUnit>>>,
) {
    val units: List<ApartmentUnit> get() = floors.flatMap { it.second }
    val soldCount: Int get() = units.count { it.status == UnitStatus.SOLD }
}

data class ProjectDetailUiState(
    val project: Project? = null,
    /** One entry per block; each holds its floors, highest floor first. */
    val blocks: List<BlockGrid> = emptyList(),
    val soldCount: Int = 0,
    val reservedCount: Int = 0,
    val totalUnits: Int = 0,
    /** Sum of sale prices of sold units. */
    val salesTotal: Double = 0.0,
    val totalExpense: Double = 0.0,
    val totalIncome: Double = 0.0,
    val recentTransactions: List<CashTransaction> = emptyList(),
    /** Every project-linked ledger entry, newest first — printable özet uses this. */
    val allTransactions: List<CashTransaction> = emptyList(),
    /** Craftsman id → name, for bartered units. */
    val craftsmanNames: Map<String, String> = emptyMap(),
)

class ProjectDetailViewModel(
    private val projectId: String,
    private val projectRepository: ProjectRepository,
    private val unitRepository: UnitRepository,
    private val transactionRepository: TransactionRepository,
    barterRepository: BarterRepository,
) : ViewModel() {

    private val _projectDeleted = MutableStateFlow(false)
    val projectDeleted: StateFlow<Boolean> = _projectDeleted.asStateFlow()

    /** Proje bazlı .xlsx içe aktarma: her satır bu projeye yazılır. */
    private val excelImport = ExcelImportController(
        scope = viewModelScope,
        transactionRepository = transactionRepository,
        projectRepository = projectRepository,
    )
    val excelImportState: StateFlow<ExcelImportState> = excelImport.state

    fun startExcelImport() = excelImport.start(
        fixedProjectId = projectId,
        fixedProjectName = uiState.value.project?.name,
        defaultDateEpochMillis = todayUtcMidnightMillis(),
    )

    fun confirmExcelImport() = excelImport.confirm()

    fun dismissExcelImport() = excelImport.dismiss()

    val uiState: StateFlow<ProjectDetailUiState> = combine(
        projectRepository.projects,
        unitRepository.unitsFor(projectId),
        transactionRepository.transactions,
        barterRepository.craftsmen,
    ) { projects, units, transactions, craftsmen ->
        val projectTxs = transactions.filter { it.projectId == projectId }
            .sortedByDescending { it.dateEpochMillis }
        ProjectDetailUiState(
            project = projects.firstOrNull { it.id == projectId },
            blocks = units
                .groupBy { it.block }
                .toList()
                .sortedBy { it.first }
                .map { (block, blockUnits) ->
                    BlockGrid(
                        name = block,
                        floors = blockUnits
                            .groupBy { it.floor }
                            .mapValues { (_, list) -> list.sortedBy { it.indexOnFloor } }
                            .toList()
                            .sortedByDescending { it.first },
                    )
                },
            soldCount = units.count { it.status == UnitStatus.SOLD },
            reservedCount = units.count { it.status == UnitStatus.RESERVED },
            totalUnits = units.size,
            salesTotal = units.filter { it.status == UnitStatus.SOLD }.sumOf { it.salePrice ?: 0.0 },
            totalExpense = projectTxs.filter { it.type == TransactionType.EXPENSE }.sumOf { it.amount },
            totalIncome = projectTxs.filter { it.type == TransactionType.INCOME }.sumOf { it.amount },
            recentTransactions = projectTxs.take(5),
            allTransactions = projectTxs,
            craftsmanNames = craftsmen.associate { it.id to it.name },
        )
    }.stateIn(
        scope = viewModelScope,
        started = SharingStarted.WhileSubscribed(5_000),
        initialValue = ProjectDetailUiState(),
    )

    /** Marks a construction stage done / not done (tap again to undo). */
    fun toggleStage(stageId: String) {
        val project = uiState.value.project ?: return
        val updated = if (stageId in project.completedStages) {
            project.completedStages - stageId
        } else {
            project.completedStages + stageId
        }
        viewModelScope.launch {
            projectRepository.updateProject(project.copy(completedStages = updated))
        }
    }

    fun updateUnit(unit: ApartmentUnit) {
        val previous = uiState.value.blocks
            .flatMap { it.units }
            .firstOrNull { it.id == unit.id }
        viewModelScope.launch {
            unitRepository.updateUnit(unit)
            syncPaymentTransactions(previous, unit)
        }
    }

    /**
     * Every received installment of a SOLD unit is ledger income with the
     * stable id "unit-pay-{paymentId}" — re-saving updates, removing a
     * payment deletes its entry. The pre-installment "unit-sale-{unitId}"
     * entry is always cleaned up (its amount now lives in the payments).
     */
    private suspend fun syncPaymentTransactions(previous: ApartmentUnit?, unit: ApartmentUnit) {
        transactionRepository.delete("unit-sale-${unit.id}")

        val oldPayments = previous?.payments.orEmpty()
        if (unit.status == UnitStatus.SOLD) {
            unit.payments.forEach { payment ->
                transactionRepository.upsert(
                    CashTransaction(
                        id = "unit-pay-${payment.id}",
                        type = TransactionType.INCOME,
                        amount = payment.amount,
                        description = buildString {
                            append("${unitDisplayLabel(unit.block, unit.number)} — ${Localization.strings.pdfPayment}")
                            if (unit.buyerName.isNotBlank()) append(" - ${unit.buyerName}")
                        },
                        category = "Daire Satışı",
                        projectId = unit.projectId,
                        dateEpochMillis = payment.dateEpochMillis,
                    )
                )
            }
            val currentIds = unit.payments.map { it.id }.toSet()
            oldPayments.filter { it.id !in currentIds }.forEach {
                transactionRepository.delete("unit-pay-${it.id}")
            }
        } else {
            // Sale reverted: remove every mirrored installment.
            (oldPayments + unit.payments).map { it.id }.distinct().forEach {
                transactionRepository.delete("unit-pay-$it")
            }
        }
    }

    /**
     * Deletes the project and its apartment grid. Ledger entries linked to
     * the project are kept — they are part of the monthly accounting history.
     */
    fun deleteProject() {
        viewModelScope.launch {
            unitRepository.deleteForProject(projectId)
            projectRepository.deleteProject(projectId)
            _projectDeleted.value = true
        }
    }
}
